import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader } from "react-bootstrap";
import { Form, Row, Col, Label } from "reactstrap";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import BreadCrumbs from "../../../components/BreadCrumbs";
import ControlledRowTextField from "../../../components/ControlledRowTextField";
import ButtonComponent from "../../../components/ButtonComponent";
import ControlledSelect from "../../../components/ControlledSelect";
import EditButtonComponent from "../../../components/EditButtonComponent";
import man from "../../../assets/icons/man.PNG";
import {
  useGetAdminDataByIdQuery,
  useUpdateAdminMutation,
  UpdateAdminRequest,
  Role
} from "../../../redux/services/adminService";
import { useLazyGetAllRolesQuery } from "../../../redux/services/rolesPermissionService";
import RoleBadge from "../../../components/RoleBadge";

interface AdminFormData {
  displayName: string;
  email: string;
  phoneNumber: string;
  roles: { value: string; label: string }[];
}

interface RoleOption {
  value: string;
  label: string;
}

const AdminView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [adminId, setAdminId] = useState<string>("");

  // API hooks
  const [updateAdmin] = useUpdateAdminMutation();
  const [getAllRoles, { isLoading: isLoadingRoles }] = useLazyGetAllRolesQuery();

  // Get admin data by ID - we'll get the ID from location state or URL params
  const { data: adminData, isLoading: isLoadingAdmin, error: adminError, refetch } = useGetAdminDataByIdQuery(
    { id: adminId },
    { skip: !adminId }
  );



  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<AdminFormData>({
    defaultValues: {
      displayName: "",
      email: "",
      phoneNumber: "",
      roles: [],
    },
  });

  // Get admin ID from route state or URL params
  useEffect(() => {
    if (location.state?.admin?.id) {
      setAdminId(location.state.admin.id);
    } else {
      // If no admin data in state, redirect to admin list
      toast.error("No admin data found");
      navigate("/admins/admins-list");
    }
  }, [location.state, navigate]);

  // Load roles on component mount
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const rolesData = await getAllRoles().unwrap();
        const transformedRoles: RoleOption[] = rolesData.map((role: any) => ({
          value: role.id,
          label: role.displayName,
        }));
        setRoleOptions(transformedRoles);
      } catch (error) {
        console.error("Error loading roles:", error);
        toast.error("Failed to load roles");
      }
    };

    loadRoles();
  }, [getAllRoles]);

  // Populate form when admin data is loaded
  useEffect(() => {
    if (adminData?.admin) {
      const admin = adminData.admin;

      // Transform roles for the form
      const transformedRoles = admin.roles?.map((role: Role) => ({
        value: role.id,
        label: role.displayName,
      })) || [];

      setValue("displayName", admin.displayName || "");
      setValue("email", admin.email || "");
      setValue("phoneNumber", admin.phoneNumber || "");
      setValue("roles", transformedRoles);
    }
  }, [adminData, setValue]);

  const onSubmit = async (data: AdminFormData) => {
    setIsSubmitLoading(true);

    try {
      // Validate required fields
      if (!data.displayName || !data.email || !data.phoneNumber) {
        toast.error("Please fill in all required fields");
        setIsSubmitLoading(false);
        return;
      }

      if (!data.roles || data.roles.length === 0) {
        toast.error("Please select at least one role");
        setIsSubmitLoading(false);
        return;
      }

      // Transform roles data for API
      const transformedRoles: Role[] = data.roles.map((role) => ({
        id: role.value,
        displayName: role.label,
        roleName: role.label.toLowerCase().replace(/\s+/g, '_'),
      }));

      const updateData: UpdateAdminRequest = {
        displayName: data.displayName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        roles: transformedRoles,
      };

      await updateAdmin({ id: adminId, data: updateData }).unwrap();

      setIsSubmitLoading(false);
      setIsEditMode(false);
      toast.success("Admin details updated successfully!");

      // Refetch admin data to show updated information
      refetch();

    } catch (error: any) {
      console.error("Error updating admin:", error);
      setIsSubmitLoading(false);

      if (error?.originalStatus === 409) {
        toast.error("This email is already in use");
      } else if (error?.data?.message) {
        toast.error(error.data.message);
      } else {
        toast.error("Failed to update admin details. Please try again.");
      }
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    // Reset form to original values
    if (adminData?.admin) {
      const admin = adminData.admin;
      const transformedRoles = admin.roles?.map((role: Role) => ({
        value: role.id,
        label: role.displayName,
      })) || [];

      setValue("displayName", admin.displayName || "");
      setValue("email", admin.email || "");
      setValue("phoneNumber", admin.phoneNumber || "");
      setValue("roles", transformedRoles);
    }
  };

  if (isLoadingAdmin) {
    return (
      <>
        <BreadCrumbs hasSubMenus={false} />
        <Card className="mt-3">
          <CardBody>
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p className="mt-3">Loading admin details...</p>
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  if (adminError) {
    return (
      <>
        <BreadCrumbs hasSubMenus={false} />
        <Card className="mt-3">
          <CardBody>
            <div className="text-center py-5">
              <p className="text-danger">Error loading admin details</p>
              <ButtonComponent
                titel="Back to Admin List"
                onClick={() => navigate("/admins/admins-list")}
              />
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  if (!adminData?.admin) {
    return (
      <>
        <BreadCrumbs hasSubMenus={false} />
        <Card className="mt-3">
          <CardBody>
            <div className="text-center py-5">
              <p>No admin data found</p>
              <ButtonComponent
                titel="Back to Admin List"
                onClick={() => navigate("/admins/admins-list")}
              />
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  const admin = adminData.admin;

  return (
    <>
      <BreadCrumbs hasSubMenus={false} />
      <Card className="mt-3">
        <CardHeader className="bg-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Admin Details</h4>         <EditButtonComponent onClick={() => setIsEditMode(!isEditMode)} />
        </CardHeader>

        {!isEditMode ? (
          // View Mode
          <CardBody>
            <Row className="mb-4">
              <Col lg="12" className="text-center mb-4">
                <img
                  src={man}
                  alt={admin.displayName}
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    border: "3px solid #590B21",
                  }}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg="3">
                <strong>Full Name:</strong>
              </Col>
              <Col lg="9">
                {admin.displayName || 'N/A'}
              </Col>
            </Row>

            <hr />

            <Row className="mb-3">
              <Col lg="3">
                <strong>Email Address:</strong>
              </Col>
              <Col lg="9">
                {admin.email || 'N/A'}
              </Col>
            </Row>

            <hr />

            <Row className="mb-3">
              <Col lg="3">
                <strong>Phone Number:</strong>
              </Col>
              <Col lg="9">
                {admin.phoneNumber || 'N/A'}
              </Col>
            </Row>

            <hr />

            <Row className="mb-3">
              <Col lg="3">
                <strong>Roles:</strong>
              </Col>
              <Col lg="9">
                <RoleBadge roles={admin.roles || []} variant="secondary" />
              </Col>
            </Row>

            <hr />

            <Row className="mb-3">
              <Col lg="3">
                <strong>Created At:</strong>
              </Col>
              <Col lg="9">
                {admin.createdAt?._seconds ?
                  new Date(admin.createdAt._seconds * 1000).toLocaleString() :
                  'N/A'
                }
              </Col>
            </Row>

            {admin.updatedAt && (
              <>
                <hr />
                <Row className="mb-3">
                  <Col lg="3">
                    <strong>Last Updated:</strong>
                  </Col>
                  <Col lg="9">
                    {new Date(admin.updatedAt._seconds * 1000).toLocaleString()}
                  </Col>
                </Row>
              </>
            )}

            <div className="d-flex justify-content-end mt-4">
              <ButtonComponent
                titel="Back to Admin List"
                onClick={() => navigate("/admins/admins-list")}
              />
            </div>
          </CardBody>
        ) : (
          // Edit Mode
          <Form onSubmit={handleSubmit(onSubmit)}>
            <CardBody>
              <ControlledRowTextField
                inputName="displayName"
                inputType="text"
                autoComplete="off"
                placeholder="Enter full name"
                label="Full Name"
                control={control}
                rules={{
                  required: "Full name is required",
                  minLength: {
                    value: 2,
                    message: "Full name must be at least 2 characters"
                  }
                }}
                errors={errors}
              />
              <Row>
                <Col lg="4">{""}</Col>
                <Col lg="8">
                  {errors.displayName && (
                    <span className="validation-message text-danger">
                      {String(errors.displayName.message)}
                    </span>
                  )}
                </Col>
              </Row>

              <hr />

              <ControlledRowTextField
                inputName="email"
                inputType="email"
                autoComplete="off"
                placeholder="Enter email address"
                label="Email Address"
                control={control}
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                    message: "Invalid email format",
                  },
                }}
                errors={errors}
              />
              <Row>
                <Col lg="4">{""}</Col>
                <Col lg="8">
                  {errors.email && (
                    <span className="validation-message text-danger">
                      {String(errors.email.message)}
                    </span>
                  )}
                </Col>
              </Row>

              <hr />

              <ControlledRowTextField
                inputName="phoneNumber"
                inputType="tel"
                autoComplete="off"
                placeholder="Enter phone number"
                label="Phone Number"
                control={control}
                rules={{
                  required: "Phone number is required",
                  pattern: {
                    value: /^[+]?[\d\s\-()]{10,}$/,
                    message: "Invalid phone number format",
                  },
                }}
                errors={errors}
              />
              <Row>
                <Col lg="4">{""}</Col>
                <Col lg="8">
                  {errors.phoneNumber && (
                    <span className="validation-message text-danger">
                      {String(errors.phoneNumber.message)}
                    </span>
                  )}
                </Col>
              </Row>

              <hr />

              <Row className="mb-3">
                <Col lg="4" className="cart-search-label-col">
                  <Label className="form-label">
                    Roles <span className="text-danger">*</span>
                  </Label>
                </Col>
                <Col lg="8">
                  <ControlledSelect
                    inputName="roles"
                    control={control}
                    optionsList={roleOptions}
                    placeholder={isLoadingRoles ? "Loading roles..." : "Select roles"}
                    isMulti={true}
                    isClear={true}
                    rules={{
                      required: "Please select at least one role",
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <Col lg="4">{""}</Col>
                <Col lg="8">
                  {errors.roles && (
                    <span className="validation-message text-danger">
                      {String(errors.roles.message)}
                    </span>
                  )}
                </Col>
              </Row>

              <hr />

              <div className="d-flex justify-content-end gap-2 mx-2 my-3">
                <ButtonComponent
                  titel="Cancel"
                  type="button"
                  onClick={handleCancel}
                />
                {isSubmitLoading ? (
                  <ButtonComponent
                    titel="Updating..."
                    icon={<FontAwesomeIcon icon={faSpinner} spin />}
                    isDisabled={true}
                  />
                ) : (
                  <ButtonComponent
                    titel="Update Admin"
                    type="submit"
                  />
                )}
              </div>
            </CardBody>
          </Form>
        )}
      </Card>
    </>
  );
};

export default AdminView;